/**
 * Signature pad with the live ink check wired in (PF-6119).
 *
 * Owns the measurement so the screen does not have to: the pad emits a PNG data URL on every stroke
 * end, this measures it and reports a status upward. The check is advisory — `submitSignatureRecapture`
 * re-measures server-side and decides — but it is what keeps the button from enabling on a signature
 * the server would reject.
 *
 * The status deliberately separates "not yet allowed to submit" from "show the error". Reporting the
 * error copy the moment a stroke ended, before measurement, made it flash for a frame on every finger
 * lift — visible enough to be raised in review. Now the button is held during `measuring` with no
 * copy shown, and a rejection is only reported after the pad has been still for `REJECTION_SETTLE_MS`,
 * so lifting the finger between two strokes of one signature never flashes red. A signature that
 * really is too small still gets the notice, just a beat later.
 *
 * Measurement is async and strokes arrive fast, so a stale result must never overwrite a newer one;
 * `latestRequestRef` drops every response but the most recent, and the same check cancels a pending
 * rejection when a new stroke lands.
 */
import type { SignatureInkThresholds } from '@physiofit-digital/physiofit_types';
import { useCallback, useEffect, useRef } from 'react';
import { judgeInk, measureInk } from '../inkValidation';
import { signatureRecaptureCopy } from '../config/copy';
import { SignatureInput } from '../ui';

/**
 * `measuring` — a stroke just ended; hold the button, show nothing.
 * `accepted` — passes the thresholds; enable the button.
 * `rejected` — fails them and the pad has been still long enough to say so.
 */
export type SignatureInkStatus = 'measuring' | 'accepted' | 'rejected';

/** Long enough to absorb the gap between strokes of one signature; short enough to feel immediate. */
const REJECTION_SETTLE_MS = 500;

type SignaturePadFieldProps = {
  value: string | null;
  thresholds: SignatureInkThresholds;
  /** Error copy to show under the pad; the screen derives it from the reported status. */
  error?: string;
  onChange: (value: string | null, status: SignatureInkStatus | null) => void;
};

export function SignaturePadField({ value, thresholds, error, onChange }: SignaturePadFieldProps) {
  const latestRequestRef = useRef(0);
  const rejectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingRejection = useCallback(() => {
    if (rejectionTimerRef.current !== null) {
      clearTimeout(rejectionTimerRef.current);
      rejectionTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearPendingRejection, [clearPendingRejection]);

  const handleChange = useCallback(
    (next: string | null) => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;
      clearPendingRejection();

      if (!next) {
        onChange(null, null);
        return;
      }
      onChange(next, 'measuring');

      const reportRejected = () => {
        rejectionTimerRef.current = setTimeout(() => {
          rejectionTimerRef.current = null;
          if (latestRequestRef.current === requestId) {
            onChange(next, 'rejected');
          }
        }, REJECTION_SETTLE_MS);
      };

      void measureInk(next)
        .then((metrics) => {
          if (latestRequestRef.current !== requestId) {
            return;
          }
          if (judgeInk(metrics, thresholds).accepted) {
            onChange(next, 'accepted');
          } else {
            reportRejected();
          }
        })
        .catch(() => {
          if (latestRequestRef.current !== requestId) {
            return;
          }
          // Decoding failed, so the ink is unknown. Treat it as a rejection rather than letting a
          // measurement bug through as an accept.
          reportRejected();
        });
    },
    [clearPendingRejection, onChange, thresholds],
  );

  return (
    <SignatureInput
      value={value}
      onChange={handleChange}
      label={signatureRecaptureCopy.sign.signatureLabel}
      labelRequired
      error={error}
    />
  );
}
